import { Dropdown } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { OrderItemSummary } from "../../../types/Order/orderItemSummary";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV } from "@fortawesome/free-solid-svg-icons";
import { DateTime } from "luxon";
import { ViewOrderItemLayers } from "../../../constant/ViewOrderItemLayers";

interface OrderItemsActionDropdownProps {
  orderItem: OrderItemSummary;
  isAdmin: boolean;
  isMyOrders?: boolean;
  hideManageRenewal?: boolean;
  viewDetails: (item: OrderItemSummary) => void;
  onDownloadClick: (orderItem: OrderItemSummary) => void;
  viewInvoice: (orderItem: OrderItemSummary) => void;
  reOrder: (orderItem: OrderItemSummary) => void;
  manageRenewal: (orderItem: OrderItemSummary) => void;
  deleteData?: (orderItem: OrderItemSummary) => void;
}

const isInrix = (product: string) => product.includes("Inrix");

const isWithinOneMonthOfExpiry = (expiry: OrderItemSummary["dataExpiry"]) => {
  if (!expiry) return false;
  const msUntilExpiry = expiry.diffNow().milliseconds;
  return msUntilExpiry > 0 && expiry.diffNow("months").months < 1;
};

const canViewData = (orderItem: OrderItemSummary) => {
  const status = orderItem.status.toLocaleLowerCase();
  const isReadyToDownload = status === "ready to download";
  const isCompleted = status === "complete";
  const isIncludedLayer = ViewOrderItemLayers.some(
    (layer) => layer === orderItem.layerName,
  );

  return (isReadyToDownload || isCompleted) && isIncludedLayer;
};

const OrderItemsActionDropdown = ({
  orderItem,
  isAdmin,
  isMyOrders = false,
  hideManageRenewal = false,
  viewDetails,
  onDownloadClick,
  viewInvoice,
  reOrder,
  manageRenewal,
  deleteData,
}: OrderItemsActionDropdownProps) => {
  const productIsInrix = isInrix(orderItem.product);
  const showManageRenewal =
    !hideManageRenewal &&
    productIsInrix &&
    isWithinOneMonthOfExpiry(orderItem.dataExpiry) &&
    isAdmin;
  const showDeleteData =
    isMyOrders && productIsInrix && orderItem.status === "Expired";
  const navigate = useNavigate();
  const showViewData = canViewData(orderItem);
  const onViewDataClick = () => {
    const url = `/view/orderitem/${encodeURIComponent(orderItem.layerName)}/${orderItem.orderItemId}`;
    navigate(url);
  };

  return (
    <Dropdown>
      <Dropdown.Toggle id="dropdown-basic">
        <FontAwesomeIcon icon={faEllipsisV} />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item
          onClick={() => viewDetails(orderItem)}
          eventKey={1}
          key={1}
        >
          {"View Details"}
        </Dropdown.Item>

        {orderItem.dataExpiry &&
          orderItem.dataExpiry.diffNow().milliseconds > 0 && (
            <Dropdown.Item
              onClick={() => onDownloadClick(orderItem)}
              eventKey={2}
              key={2}
            >
              {"Download"}
            </Dropdown.Item>
          )}

        {orderItem.invoiceUrl && (
          <Dropdown.Item
            onClick={() => viewInvoice(orderItem)}
            eventKey={3}
            key={3}
          >
            {"View Invoice"}
          </Dropdown.Item>
        )}

        {((productIsInrix && isWithinOneMonthOfExpiry(orderItem.dataExpiry)) ||
          (!productIsInrix &&
            orderItem.dataExpiry &&
            orderItem.dataExpiry.diffNow().milliseconds < 0)) && (
          <Dropdown.Item
            onClick={() => reOrder(orderItem)}
            eventKey={4}
            key={4}
          >
            Re-Order Data
          </Dropdown.Item>
        )}

        {showManageRenewal && (
          <Dropdown.Item
            onClick={() => manageRenewal(orderItem)}
            eventKey={5}
            key={5}
          >
            Manage Renewal
          </Dropdown.Item>
        )}

        {showDeleteData && deleteData && (
          <Dropdown.Item
            onClick={() => deleteData(orderItem)}
            eventKey={6}
            key={6}
          >
            Delete Data
          </Dropdown.Item>
        )}
        {showViewData && (
          <Dropdown.Item onClick={onViewDataClick} eventKey={7} key={7}>
            View Data
          </Dropdown.Item>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default OrderItemsActionDropdown;
